import { Box } from '@mui/material';
import {
  AskInstructionModel,
  BulletedListType,
  ComprehensionPassage,
  ContentElementType,
  ExcerptType,
  HeadingType,
  ImageOption,
  ImageType,
  InstructionElementType,
  McqMultipleContentModel,
  McqSingleContentModel,
  NumberedListType,
  Question,
  QuestionContentModel,
  ResourceCategoryEnum,
  SubHeadingType,
  TableType,
  TextBoxType,
  TextCenterType,
  TextLeftType,
  TextOption,
  TextRightType,
  TextType,
} from '@protos/content_management/content.db_pb';
import React, { CSSProperties } from 'react';
import {
  getMediaBasePath,
  shouldIncludeCredentialsInRequest,
} from '../../../../commonUtils/images';
import { ElementHeadingTag } from '../../../composites/ElementRenderer/ElementHeadingTag';
import { ImagePopup } from '../../../composites/ElementRenderer/ImagePopup';
import { TableElement } from '../../../composites/ElementRenderer/Table';
import ImageWrapper, { isGifMp4 } from '../../../elements/ImageWrapper';
import { GeneoKatex } from '../GeneoKatex';
import {
  BlankCounter,
  getSolutionText,
  numberToColumnTitle,
} from '../question-utils';

const printableStyles: CSSProperties = {
  breakInside: 'avoid', // For modern browsers and html2pdf.js
  pageBreakInside: 'avoid', // For legacy support
  // WebkitColumnBreakInside: 'avoid', // For Chrome/Safari
  // MozPageBreakInside: 'avoid', // For Firefox
};

const styles = {
  textBoxWrapper: {
    // Add any required styles here or import from QuestionsList if needed
  },
  textElementsWrapper: {
    // Add any required styles here or import from QuestionsList if needed
  },
  textInput: {
    minWidth: '70px',
    margin: '0 4px',
    borderBottom: '1px solid #000',
    fontSize: 'inherit',
    display: 'inline-block',
  },
};

const renderElements = (
  elements?: ContentElementType[],
  isFibText?: boolean,
  resourceCategory?: ResourceCategoryEnum,
  showMedia: boolean = true,
  imageResizeEnabled?: boolean
) => {
  const textElements = (
    texts: string[],
    sx: CSSProperties | undefined,
    variant: any,
    component?: any
  ): JSX.Element[] => {
    return texts.map((text, index) => {
      return (
        <Box component={component} key={index} style={sx}>
          <PrintableText style={typeof variant === 'object' ? variant : {}}>
            {text}
          </PrintableText>
        </Box>
      );
    });
  };
  const splitText = (t: string): string[] =>
    t.split('<br>').filter((text) => text);

  const blankCounter = new BlankCounter();

  const renderFibText = (
    qParts: string[],
    style: CSSProperties | undefined,
    variant: any,
    wrapperComponent?: any
  ) => {
    return (
      <Box component={wrapperComponent}>
        {qParts.map((part, index) => {
          if (index === 0) {
            return (
              part && textElements(splitText(part), style, variant, 'span')
            );
          }
          const ct = blankCounter ? blankCounter.next() : 0;
          return (
            <React.Fragment key={index}>
              <Box style={styles.textInput} />
              {part &&
                textElements(
                  splitText(part),
                  style,
                  variant || 'elementBodyText',
                  'span'
                )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  const renderElement = (element: ContentElementType) => {
    const elementType =
      element instanceof InstructionElementType
        ? element.instruction.case
        : element.model.case;
    const elementValue =
      element instanceof InstructionElementType
        ? element.instruction.value
        : element.model.value;
    const elem: { text?: string; style?: CSSProperties; variant?: any } = {};
    const BlankTextRegExp = /_{3,}/g;
    const tagName =
      elementType === 'teacherNote'
        ? 'NOTE'
        : elementType === 'ask' || elementType == 'askQuestion'
        ? 'QUESTION'
        : elementType === 'explain'
        ? 'EXPLANATION'
        : elementType?.toUpperCase();
    switch (elementType) {
      case 'text': {
        if (elementValue instanceof TextType) {
          elem.text = elementValue.text;
        }
      }
      /* falls through */
      case 'textCenter': {
        if (elementValue instanceof TextCenterType) {
          elem.text = elementValue.textCenter;
          elem.style = {
            textAlign: 'center',
            fontSize: '1em',
          };
        }
      }
      /* falls through */
      case 'textLeft': {
        if (elementValue instanceof TextLeftType) {
          elem.text = elementValue.textLeft;
          fontSize: '1em';
        }
      }
      /* falls through */
      case 'textRight': {
        if (elementValue instanceof TextRightType) {
          elem.text = elementValue.textRight;
          elem.style = {
            textAlign: 'right',
            fontSize: '1em',
          };
        }
      }
      /* falls through */
      case 'textBox': {
        if (elementValue instanceof TextBoxType) {
          elem.text = elementValue.boxText;
          elem.style = {
            fontSize: '1em',
          };
        }
      }
      /* falls through */
      case 'heading': {
        if (elementValue instanceof HeadingType) {
          elem.text = elementValue.heading;
          elem.style = {
            fontFamily: 'Poppins',
            color: '#1D1D1D',
            fontWeight: '800',
            fontSize: '1.4em',
          };
        }
      }
      /* falls through */
      case 'subHeading': {
        if (elementValue instanceof SubHeadingType) {
          elem.text = elementValue.subHeading;
          elem.style = {
            fontSize: '1em',
          };
        }
        const { text, style, variant } = elem;
        const qParts = text?.split(BlankTextRegExp) || [];
        if (isFibText) {
          return renderFibText(qParts, style, variant || 'elementBodyText');
        }
        return text ? (
          <Box
            style={
              elementType == 'textBox'
                ? styles.textBoxWrapper
                : styles.textElementsWrapper
            }
          >
            {textElements(splitText(text), style, variant || {})}
          </Box>
        ) : null;
      }
      case 'teacherImage':
      case 'image':
        if (elementValue instanceof ImageType) {
          const imageUrl = getMediaBasePath(
            elementValue.imageUrl,
            resourceCategory ===
              ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
              ? 'userDataBucket'
              : 'processedMediaBucket'
          );
          if (!showMedia) {
            return null;
          }
          return imageResizeEnabled ? (
            <ResizableImage url={imageUrl} alt={elementValue.imageName} />
          ) : (
            <ImageWithBase64 url={imageUrl} alt={elementValue.imageName} />
          );
        }
        return null;
      case 'video':
      case 'teacherVideo':
      case 'audio':
        return null;
      case 'numberList':
        if (elementValue instanceof NumberedListType) {
          return (
            <Box
              component="ol"
              style={{
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                paddingLeft: '16px',
                // 'li::marker' is not supported in style, so skip or use global CSS if needed
              }}
            >
              {elementValue.listItems.map((text, index) => {
                const qParts = text?.split(BlankTextRegExp) || [];
                if (isFibText) {
                  return renderFibText(qParts, {}, 'elementBodyText', 'li');
                }
                return (
                  <li
                    key={index}
                    style={{
                      marginBottom:
                        index === elementValue.listItems.length - 1
                          ? '0'
                          : '15px',
                    }}
                  >
                    {textElements(
                      splitText(text),
                      {},
                      'elementBodyText',
                      'span'
                    )}
                  </li>
                );
              })}
            </Box>
          );
        }
        return null;
      case 'bulletList':
        if (elementValue instanceof BulletedListType) {
          return (
            <Box
              component="ul"
              style={{
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                paddingLeft: '16px',
                // 'li::marker' is not supported in style, so skip or use global CSS if needed
              }}
            >
              {elementValue.listItems.map((text, index) => {
                const qParts = text?.split(BlankTextRegExp) || [];
                if (isFibText) {
                  return renderFibText(qParts, {}, 'elementBodyText', 'li');
                }
                return (
                  <li
                    key={index}
                    style={{
                      marginBottom:
                        index === elementValue.listItems.length - 1
                          ? '0'
                          : '15px',
                    }}
                  >
                    {textElements(
                      splitText(text),
                      {},
                      'elementBodyText',
                      'span'
                    )}
                  </li>
                );
              })}
            </Box>
          );
        }
        return null;
      case 'askQuestion':
        if (typeof elementValue == 'string') {
          return (
            <>
              {tagName && <ElementHeadingTag text={tagName} />}
              {textElements(
                splitText(elementValue),
                {},
                'elementBodyText',
                'span'
              )}
            </>
          );
        }
        return null;
      case 'askAnswer':
        if (typeof elementValue == 'string') {
          return (
            <>
              <PrintableText
                style={{
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  fontSize: '1.17em',
                }}
              >
                Answer
              </PrintableText>
              {textElements(
                splitText(elementValue),
                {},
                'elementBodyText',
                'div'
              )}
            </>
          );
        }
        return null;
      case 'ask':
        if (elementValue instanceof AskInstructionModel) {
          return (
            <>
              {tagName && <ElementHeadingTag text={tagName} />}
              {textElements(
                splitText(elementValue.question),
                {},
                'elementBodyText',
                'span'
              )}
              <PrintableText
                style={{
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  fontSize: '1.17em',
                }}
              >
                Answer
              </PrintableText>
              {textElements(
                splitText(elementValue.answer),
                {},
                'elementBodyText',
                'div'
              )}
            </>
          );
        }
        return null;
      case 'do':
      case 'teacherNote':
      case 'note':
      case 'explain':
      case 'say':
      case 'discuss':
        if (typeof elementValue == 'string') {
          return (
            <>
              {tagName && <ElementHeadingTag text={tagName} />}
              {textElements(
                splitText(elementValue),
                {},
                'elementBodyText',
                'span'
              )}
            </>
          );
        }
        return null;
      case 'comprehensionPassage':
        if (elementValue instanceof ComprehensionPassage) {
          return (
            <Box>
              {textElements(
                splitText(elementValue.comprehensionPassageText),
                {},
                'elementBodyText',
                'span'
              )}
            </Box>
          );
        }
        return null;
      case 'table':
        if (elementValue instanceof TableType && elementValue.table) {
          return (
            <TableElement
              resourceCategory={resourceCategory}
              table={elementValue.table}
            />
          );
        }
        return null;
      case 'excerpt':
        if (elementValue instanceof ExcerptType) {
          return (
            <Box>
              {textElements(
                splitText(elementValue.excerpt),
                {},
                'elementBodyText',
                'span'
              )}
            </Box>
          );
        }
        return null;
      default:
        return null;
    }
  };
  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {elements?.map((element, i) => {
        return (
          <div
            style={{
              ...printableStyles,
              marginBottom: '5px',
            }}
            key={i}
          >
            {renderElement(element)}
          </div>
        );
      })}
    </Box>
  );
};

// Helper function to fetch image as base64
export const fetchImageAsBase64 = async (
  url: string
): Promise<string | null> => {
  // console.log('fetching image as base64', url);
  try {
    const reqOptions: RequestInit = {};
    if (shouldIncludeCredentialsInRequest(url)) {
      reqOptions.credentials = 'include';
    }
    const response = await fetch(url, {
      ...reqOptions,
    });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to fetch image as base64:', error);
    return null;
  }
};

// Component to render image as base64
const ImageWithBase64: React.FC<{
  url: string;
  alt?: string;
  isSmallImage?: boolean;
}> = ({ url, alt, isSmallImage }) => {
  url = isGifMp4(url) ? url.replace('mp4', 'jpg') : url;
  const [base64, setBase64] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;
    if (url.startsWith('data:')) {
      setBase64(url);
    } else {
      fetchImageAsBase64(url).then((data) => {
        if (isMounted) setBase64(data);
      });
    }
    return () => {
      isMounted = false;
    };
  }, [url]);

  if (!base64) {
    return (
      <Box
        style={{
          width: '100%',
          minHeight: '100px',
          background: '#eee',
          ...printableStyles,
        }}
        className="avoid-break-element"
      />
    );
  }

  return (
    <Box
      component="img"
      src={base64}
      style={{
        maxHeight: isSmallImage ? '100px' : '250px',
        maxWidth: '100%',
        margin: '0 auto',
        ...printableStyles,
      }}
      className="avoid-break-element"
      alt={alt}
    />
  );
};

const resizableStyles = {
  wrapper: {
    display: 'inline-block',
    mx: 'auto',
    position: 'relative',
  } as const,
  resizeBox: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    cursor: 'pointer',
    padding: '7px',
    border: '1px solid #007CDC',
    backgroundColor: '#DBF0FF',
    borderRadius: '8px',
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    justifyContent: 'center',
  } as const,
};

const ResizableImage: React.FC<{ url: string; alt?: string }> = ({
  url,
  alt,
}) => {
  const [open, setOpen] = React.useState(false);
  return (
    <Box sx={resizableStyles.wrapper}>
      <ImageWithBase64 url={url} alt={alt} />
      <Box sx={resizableStyles.resizeBox} onClick={() => setOpen(true)}>
        <Box
          component="span"
          sx={{
            fontSize: 12,
            color: '#007CDC',
            display: { xs: 'none', md: 'inline' },
          }}
        >
          View Full Image
        </Box>
        <ImageWrapper
          name="resizeBox"
          type="png"
          parentFolder="icons"
          styles={{ height: '15px', width: '15px', display: 'block' }}
        />
      </Box>
      <ImagePopup
        imageName={alt}
        imageUrl={url}
        open={open}
        handleClose={() => setOpen(false)}
      />
    </Box>
  );
};

// PrintableText: renders a div with parsed HTML using GeneoKatex
const PrintableText: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ children, style }) => {
  let text = '';
  if (children) {
    text = children.toString();
  }
  return (
    <div
      style={{
        ...style,
        ...printableStyles,
      }}
      className="avoid-break-element"
      dangerouslySetInnerHTML={{ __html: new GeneoKatex(text).parsedHtml() }}
    />
  );
};

interface PrintableQuestionProps {
  ques: Question;
  number?: number;
  marks?: number;
  showAnswer?: boolean;
  isAiGenerated?: boolean;
  showMedia?: boolean;
  hideOptions?: boolean;
  hideQuestionNumber?: boolean;
  imageResizeEnabled?: boolean;
}

const PrintableQuestion: React.FC<PrintableQuestionProps> = ({
  ques,
  number,
  marks,
  showAnswer,
  isAiGenerated,
  showMedia,
  hideOptions,
  hideQuestionNumber,
  imageResizeEnabled,
}) => {
  const question = new QuestionContentModel(ques?.question);
  const elements = question?.model.value?.commonQuestionContent?.elements;
  const options =
    question?.model.value instanceof McqSingleContentModel ||
    question?.model.value instanceof McqMultipleContentModel
      ? question?.model.value.options
      : [];
  const isFibText = question?.model.case === 'fibContentModel';
  const solution = question.model.value?.commonQuestionContent?.solution || [];
  const solutionWithoutVideo = solution.filter((e) => e.model.case !== 'video');
  let solutionText = getSolutionText(ques, undefined, true);
  solutionText = typeof solutionText === 'string' ? solutionText.trim() : '';
  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: '5px',
      }}
    >
      {!hideQuestionNumber && (
        <Box
          style={{
            ...printableStyles,
            paddingRight: '10px',
            minWidth: '30px',
            textAlign: 'right',
          }}
          className="avoid-break-element"
        >
          <PrintableText style={{ fontSize: '1em' }}>
            {number ? 'Q.' + number : null}
          </PrintableText>
        </Box>
      )}
      <Box style={{ display: 'flex', flexDirection: 'column' }}>
        <Box style={{ marginBottom: '5px' }}>
          {renderElements(
            elements,
            isFibText,
            undefined,
            showMedia,
            imageResizeEnabled
          )}
        </Box>
        {!hideOptions && (
          <Box>
            {options.map((option, index) => {
              const opt = option.optionType.value;
              if (opt instanceof TextOption) {
                return (
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '5px',
                      ...printableStyles,
                    }}
                    className="avoid-break-element"
                    key={'option-' + index}
                  >
                    <PrintableText>
                      {numberToColumnTitle(index) + ')'}
                    </PrintableText>
                    <PrintableText>{opt?.optionText}</PrintableText>
                  </Box>
                );
              }
              if (opt instanceof ImageOption) {
                const imageUrl = getMediaBasePath(
                  opt.image?.imageUrl,
                  'processedMediaBucket'
                );
                return (
                  <Box key={index} display="flex" alignItems="center">
                    <PrintableText style={{ marginRight: '10px' }}>
                      {numberToColumnTitle(index)})
                    </PrintableText>
                    <ImageWithBase64
                      url={imageUrl}
                      alt={opt?.image?.imageName}
                      isSmallImage={true}
                    />
                  </Box>
                );
              }
              return null;
            })}
          </Box>
        )}
        {showAnswer && solutionText && (
          <Box style={{ marginBottom: '5px' }}>
            <PrintableText>
              {'Answer: ' + getSolutionText(ques, undefined, true)}
            </PrintableText>
          </Box>
        )}
        {showAnswer && solutionWithoutVideo.length > 0 && (
          <Box sx={{ marginBottom: '5px' }}>
            <PrintableText>{'Solution: '}</PrintableText>
            {renderElements(
              solutionWithoutVideo,
              isFibText,
              undefined,
              showMedia,
              imageResizeEnabled
            )}
          </Box>
        )}
      </Box>
      <Box
        style={{
          ...printableStyles,
          marginLeft: 'auto',
          paddingLeft: '10px',
          paddingRight: '10px',
        }}
        className="avoid-break-element"
      >
        <PrintableText style={{ fontSize: '1em' }}>
          {marks ? `[${marks}]` : null}
        </PrintableText>
      </Box>
    </Box>
  );
};

export default PrintableQuestion;
